import { DateTime } from "luxon";
import type { RouteLocationRaw } from "vue-router";
import type {
  SetupLevel,
  SetupLevelProgress,
  SetupLockedReason,
} from "@/services/setup_progress";

export interface SetupItemCopy {
  title: string;
  value: string;
  // The item's tour (same key) picks up from this route.
  to: RouteLocationRaw;
}

// Keyed by setup_progress/registry.py. An item without copy here is not
// shown, so a backend item can ship before its copy.
export const SETUP_ITEM_COPY: Record<string, SetupItemCopy> = {
  connect_channels: {
    title: "Connect your channels",
    value: "See all your marketing in one place",
    to: { name: "select-channel" },
  },
  invite_teammate: {
    title: "Invite a teammate",
    value: "Share insights without exporting screenshots",
    to: { name: "general" },
  },
  auto_tag_rule: {
    title: "Set up an auto-tag rule",
    value: "New campaigns get tagged daily, with no manual work",
    to: { name: "tags" },
  },
  goal_or_kpi: {
    title: "Set a goal or KPI",
    value: "Know at a glance whether you're on track",
    to: { name: "goals" },
  },
  add_competitor: {
    title: "Add a competitor",
    value: "Benchmark against competitors as their data starts flowing",
    to: { name: "competitors" },
  },
  plan_budget: {
    title: "Plan a budget",
    value: "Catch over- and underspend early",
    to: { name: "budget_tool" },
  },
  create_client_organization: {
    title: "Add another organization",
    value: "Run every client, brand or market from one place",
    to: { name: "hub" },
  },
  act_on_recommendation: {
    title: "Act on a recommendation",
    value: "Turn Fyr's suggestions into results",
    to: { name: "all-recommendations" },
  },
  ask_ai_assistant: {
    title: "Ask Automagic AI",
    value: "Get answers about your data in plain language",
    to: { name: "home" },
  },
  build_dashboard: {
    title: "Build a dashboard, or favorite one",
    value: "Your numbers, your layout, or the ones you check most",
    to: { name: "dashboards" },
  },
  tag_entities: {
    title: "Create or apply a tag",
    value: "Compare campaigns by theme, not just by platform",
    to: { name: "tags" },
  },
  add_annotation: {
    title: "Add an annotation",
    value: "Explain spikes so the team knows why",
    to: { name: "ad-performance-overview" },
  },
  set_up_conversions: {
    title: "Set up conversions",
    value: "Count the actions that matter to your business",
    to: { name: "edit-conversions" },
  },
  calculated_metric: {
    title: "Create a calculated metric",
    value: "Track your own formulas, like blended ROAS",
    to: { name: "calculated-metrics" },
  },
  share_dashboard_link: {
    title: "Share a dashboard link",
    value: "Give stakeholders a live view without an account",
    to: { name: "dashboards" },
  },
  group_resources: {
    title: "Group your channels",
    value: "Combine accounts that belong together",
    to: { name: "resources" },
  },
  custom_data_import: {
    title: "Import your own data",
    value: "Bring offline and CRM numbers in from a sheet",
    to: { name: "resources" },
  },
  site_score: {
    title: "Check your Site Score",
    value: "Find the fixes that lift your website the most",
    to: { name: "site-score" },
  },
  share_of_search: {
    title: "Track Share of Search",
    value: "See how often people search for you versus competitors",
    to: { name: "share-of-search" },
  },
  pin_kpi: {
    title: "Pin a KPI",
    value: "Keep your most important number on Home",
    to: { name: "goals" },
  },
  ai_visibility: {
    title: "Set up AI Visibility",
    value: "See how AI assistants talk about your brand",
    to: { name: "ai_visibility_overview" },
  },
  mcp_connection: {
    title: "Connect Fyr to your AI assistant",
    value: "Ask Claude or ChatGPT about your Fyr data",
    to: { name: "mcp-applications" },
  },
  share_budget_plan: {
    title: "Share a budget plan",
    value: "Let the people who sign off on spend follow the plan",
    to: { name: "budget_tool" },
  },
  change_timezone: {
    title: "Check your timezone",
    value: "Make daily numbers line up with your working day",
    to: { name: "general" },
  },
  change_currency: {
    title: "Check your display currency",
    value: "Report spend in the currency you budget in",
    to: { name: "general" },
  },
  refresh_resource: {
    title: "Refresh a channel's data",
    value: "Pull the latest numbers when you can't wait",
    to: { name: "resources" },
  },
  notification_emails: {
    title: "Choose your notifications",
    value: "Get the alerts you want and none you don't",
    to: { name: "profile" },
  },
  utm_builder: {
    title: "Build tagged links",
    value: "Keep campaign tracking consistent with the UTM builder",
    to: { name: "utm-builder" },
  },
  make_home_yours: {
    title: "Make Home yours",
    value: "Start each day with what matters to you",
    to: { name: "home" },
  },
  profile_picture: {
    title: "Add a profile picture",
    value: "Make it personal, so teammates see who did what",
    to: { name: "profile" },
  },
  organization_logo: {
    title: "Add your organization's logo",
    value:
      "Spot the right organization at a glance in the sidebar and switcher",
    to: { name: "general" },
  },
};

export function earnedElsewhereLine(
  levelName: string,
  organizationName: string | null,
  reachedAt: string | null,
): string {
  const where = organizationName ?? "another organization";
  const date = reachedAt ? DateTime.fromISO(reachedAt) : null;
  const when = date
    ? ` on ${date.toFormat(date.hasSame(DateTime.now(), "year") ? "d LLL" : "d LLL yyyy")}`
    : "";
  return `${levelName} earned in ${where}${when}. Your user level follows you; the open items here are for this organization.`;
}

export const LOCKED_REASON_COPY: Record<SetupLockedReason, string> = {
  needs_resources: "Available once a channel is connected",
  waiting_for_data: "Available once we've gathered your data",
};

// `why` is the payoff of the next level, shown with the "Next:" step.
export const LEVEL_COPY: Record<
  SetupLevel,
  { name: string; meaning: string; why: string | null }
> = {
  explorer: {
    name: "Explorer",
    meaning:
      "You're getting started. Every essential you finish gives Fyr more to work with, so its numbers, alerts and suggestions get more useful to you.",
    why: "Fyr then has your channels, goals and team in one place, so it can flag what needs your attention.",
  },
  wayfinder: {
    name: "Wayfinder",
    meaning:
      "Fyr has what it needs to work for you: your channels report in one place, your goals are tracked and your team can act on what Fyr finds.",
    why: "These go past reporting: watch competitors, measure conversions and share results without exporting a thing.",
  },
  navigator: {
    name: "Navigator",
    meaning:
      "You get more out of Fyr than most: beyond your own numbers, you're tracking competitors, conversions and the metrics that matter to your business.",
    why: "Lightkeepers get new beta features before anyone else, and their feedback shapes what we build.",
  },
  lightkeeper: {
    name: "Lightkeeper",
    // Only someone who has reached it reads this, so it congratulates; the
    // line under it then only says how to keep it.
    meaning:
      "🎉 Congrats, you're at the top: you get new beta features before anyone else, and your feedback shapes what we build.",
    why: null,
  },
};

export function toNavigator(level: SetupLevelProgress): string {
  const left = Math.max(level.advancedNeeded - level.advancedDone, 1);
  return level.advancedDone > 0
    ? `Try ${left} more advanced feature${left === 1 ? "" : "s"} to become a Navigator`
    : `Try ${level.advancedNeeded} advanced features to become a Navigator`;
}

export function lightkeeperActivity(level: SetupLevelProgress): string {
  const days = `${level.activeDaysPerWeek} days a week`;
  return level.activeWeeksNeeded >= level.activeWeeksWindow
    ? `${days} for ${level.activeWeeksWindow} weeks in a row`
    : `${days} in ${level.activeWeeksNeeded} of the last ${level.activeWeeksWindow} weeks`;
}

export function toLightkeeper(level: SetupLevelProgress): string {
  return `Be active ${lightkeeperActivity(level)} to become a Lightkeeper`;
}

export function keepTheLightBurningLine(level: SetupLevelProgress): string {
  return `Stay active ${level.activeDaysPerWeek} days a week to keep the light burning`;
}

export function atLightkeeperLine(level: SetupLevelProgress): string {
  return `🎉 Congrats, you're at the top! ${keepTheLightBurningLine(level)}`;
}

export const LEVEL_UP_COPY: Record<
  Exclude<SetupLevel, "explorer">,
  { title: string; body: string; next: (level: SetupLevelProgress) => string }
> = {
  wayfinder: {
    title: "You're a Wayfinder",
    body: LEVEL_COPY.wayfinder.meaning,
    next: (level) => `${toNavigator(level)}. ${LEVEL_COPY.wayfinder.why}`,
  },
  navigator: {
    title: "You're a Navigator",
    body: LEVEL_COPY.navigator.meaning,
    next: (level) => `${toLightkeeper(level)}. ${LEVEL_COPY.navigator.why}`,
  },
  lightkeeper: {
    title: "You're a Lightkeeper!",
    body: "That's the top level, and it comes with early access: you get new beta features before anyone else, and your feedback shapes what we build.",
    next: (level) => `${keepTheLightBurningLine(level)}.`,
  },
};

export const LEVELS_INTRO_COPY = {
  welcomeTitle: (firstName: string | undefined) =>
    firstName ? `Welcome to Fyr, ${firstName}` : "Welcome to Fyr",
  welcomeBody:
    "All your marketing in one place. We'll show you what's worth setting up first, and every step earns you a user level. Only you can see yours.",
  title: (level: SetupLevel) =>
    level === "explorer"
      ? "Meet your setup guide"
      : `You're already a ${LEVEL_COPY[level].name}`,
  body: (level: SetupLevel) =>
    level === "explorer"
      ? "Fyr now shows what's worth setting up next, and every step earns you a user level. Only you can see yours."
      : `Fyr now rewards what you've set up with user levels. Based on what you've already done, you start as a ${LEVEL_COPY[level].name}. Only you can see yours.`,
  next: (level: SetupLevelProgress) =>
    level.current === "explorer"
      ? `Finish the essentials to become a Wayfinder. ${LEVEL_COPY.explorer.why}`
      : LEVEL_UP_COPY[level.current].next(level),
};

// Outside the tour registry: shown when that registry is what failed to load.
export const TOUR_LOAD_FAILED_COPY = {
  title: "This guide couldn't load",
  body: "Reload the page and try again.",
};
